import React from "react";
import { escapeHtml } from "../../../utils/printHtml";
import { dmy, money } from "./feeCollectionService";

/** Rows shown on a receipt, shared by the on-screen card and the printable copy. */
const receiptRows = (r) => {
  const rows = [
    ["Student", r.studentName + (r.admissionNumber ? ` (${r.admissionNumber})` : "")],
    ["Class", r.className || "—"],
    ["Payment mode", r.paymentMode],
  ];
  // Older / parent receipts may name a fee category; office receipts don't.
  if (r.category) rows.splice(2, 0, ["Fee category", r.category]);
  if (r.paymentMode === "Cheque" && r.cheque) {
    rows.push(["Bank name", r.cheque.bankName]);
    rows.push(["Branch", r.cheque.branch]);
    rows.push(["Cheque number", r.cheque.chequeNo]);
    rows.push(["Cheque date", dmy(r.cheque.chequeDate)]);
    if (r.chequeStatus) rows.push(["Cheque status", r.chequeStatus]);
  }
  if (r.referenceNumber) rows.push(["Reference no.", r.referenceNumber]);
  if (r.source === "online" && r.paidBy) rows.push(["Paid by", r.paidBy]);
  return rows;
};

/** On-screen receipt (also the live preview while collecting). */
export function ReceiptCard({ receipt, draft }) {
  const r = receipt;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #d9d9d9",
        borderRadius: 14,
        overflow: "hidden",
        fontSize: 14,
        color: "#1a1a1a",
        opacity: draft ? 0.92 : 1,
      }}
    >
      <div style={{ padding: "18px 20px", borderBottom: "1px dashed #c4c4c4", textAlign: "center" }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>{r.schoolName || "School name"}</div>
        {r.schoolAddress && <div style={{ color: "#555", fontSize: 13, marginTop: 2 }}>{r.schoolAddress}</div>}
        <div style={{ marginTop: 10, fontSize: 12, letterSpacing: 0.8, textTransform: "uppercase", color: "#555", fontWeight: 700 }}>
          Fee receipt{r.source === "online" ? " · Online" : ""}
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 20px", background: "#f5f5f5", fontSize: 13 }}>
        <span>
          Receipt no. <b>{r.receiptNo || (draft ? "Auto" : "")}</b>
        </span>
        <span>
          Date <b>{dmy(r.receiptDate) || "Today"}</b>
        </span>
      </div>
      <dl style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "8px 12px", margin: 0, padding: "16px 20px" }}>
        {receiptRows(r).map(([k, v]) => (
          <React.Fragment key={k}>
            <dt style={{ color: "#555" }}>{k}</dt>
            <dd style={{ margin: 0, fontWeight: 500, wordBreak: "break-word" }}>{v || "—"}</dd>
          </React.Fragment>
        ))}
      </dl>
      <div style={{ padding: "14px 20px", borderTop: "1px solid #ececec" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={{ color: "#555" }}>Amount received</span>
          <span style={{ fontSize: 24, fontWeight: 700 }}>{money(r.amount)}</span>
        </div>
        <div style={{ color: "#555", fontSize: 13, marginTop: 4, fontStyle: "italic" }}>{r.amountInWords || "—"}</div>
      </div>
      {r.source !== "online" && (
        <div style={{ padding: "12px 20px", borderTop: "1px solid #ececec", fontSize: 13, color: "#555" }}>
          Collected by <b style={{ color: "#1a1a1a" }}>{r.collectedBy || "—"}</b>
        </div>
      )}
    </div>
  );
}

/** Printable receipt (browser print → "Save as PDF" downloads it). */
export function printReceipt(r) {
  const rows = receiptRows(r)
    .map(([k, v]) => `<tr><th>${escapeHtml(k)}</th><td>${escapeHtml(v || "—")}</td></tr>`)
    .join("");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Receipt ${escapeHtml(r.receiptNo)}</title>
<style>
body{font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;margin:0;padding:32px}
.box{max-width:620px;margin:0 auto;border:1px solid #bbb;border-radius:10px;overflow:hidden}
.head{text-align:center;padding:20px;border-bottom:1px dashed #999}
.head h1{font-size:20px;margin:0}.head p{margin:4px 0 0;color:#555;font-size:13px}
.tag{margin-top:10px;font-size:12px;letter-spacing:.8px;text-transform:uppercase;color:#555;font-weight:bold}
.meta{display:flex;justify-content:space-between;padding:10px 20px;background:#f2f2f2;font-size:13px}
table{width:100%;border-collapse:collapse;font-size:14px}th,td{text-align:left;padding:7px 20px;vertical-align:top}
th{color:#555;font-weight:normal;width:170px}
.amt{padding:14px 20px;border-top:1px solid #ddd}.amt b{font-size:22px;float:right}
.words{color:#555;font-style:italic;font-size:13px;margin-top:6px;clear:both}
.foot{display:flex;justify-content:space-between;padding:28px 20px 16px;font-size:13px;color:#555}
</style></head><body><div class="box">
<div class="head"><h1>${escapeHtml(r.schoolName)}</h1><p>${escapeHtml(r.schoolAddress)}</p>
<div class="tag">Fee receipt${r.source === "online" ? " · Online" : ""}</div></div>
<div class="meta"><span>Receipt no. <b>${escapeHtml(r.receiptNo)}</b></span><span>Date <b>${escapeHtml(dmy(r.receiptDate))}</b></span></div>
<table>${rows}</table>
<div class="amt">Amount received <b>${escapeHtml(money(r.amount))}</b><div class="words">${escapeHtml(r.amountInWords)}</div></div>
<div class="foot"><span>${r.source === "online" ? "Paid online" : `Collected by: ${escapeHtml(r.collectedBy || "")}`}</span><span>Signature</span></div>
</div></body></html>`;

  // Print from a hidden frame so pop-up blockers don't get in the way.
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  frame.srcdoc = html;
  frame.onload = () => {
    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } finally {
      setTimeout(() => frame.remove(), 60000);
    }
  };
  document.body.appendChild(frame);
}
